// Setting up a league: name, seats, format, how unplayed games are voided, and the army builder.
// Options not built yet are shown greyed out as "later".
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { createLeague } from '../../leagues/api';
import type { LeagueSettings } from '../../leagues/api';
import { LeagueHeader, Seg, storedName, storeName, leagueGameOptions, gameOptionsText } from './parts';
import CtfSettings from '../games/capture-the-flag/CtfSettings';
import { TEAM_KEYS, teamName } from '../../leagues/armyBuilder';
import { CTF_ROOM_DEFAULT_CONFIG } from '../../games/capture-the-flag/room';
import type { CtfRoomConfig } from '../../games/capture-the-flag/room';
import '../site/site.css';

export default function LeagueNewPage() {
  const navigate = useNavigate();
  const [name, setName] = React.useState('');
  const [me, setMe] = React.useState(storedName);
  const [seats, setSeats] = React.useState(4);
  const [format, setFormat] = React.useState<LeagueSettings['format']>('single');
  const [voidAfter, setVoidAfter] = React.useState<number | null>(7);
  const [choices, setChoices] = React.useState<2 | 3>(2);
  const [armies, setArmies] = React.useState<'builder' | 'fixed' | 'pick'>('builder');
  const [team, setTeam] = React.useState(TEAM_KEYS[0]);
  const [improve, setImprove] = React.useState(true);
  const [gameConfig, setGameConfig] = React.useState<CtfRoomConfig>(CTF_ROOM_DEFAULT_CONFIG);
  const gameOptions = leagueGameOptions(gameConfig);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const per = seats % 2 ? seats : seats - 1;
  const rounds = format === 'double' ? per * 2 : per;
  const games = format === 'double' ? seats * (seats - 1) : seats * (seats - 1) / 2;

  const create = async () => {
    setBusy(true); setError(null);
    try {
      storeName(me.trim());
      const L = await createLeague(name, me, { seats, format, voidAfterDays: voidAfter, choices, improve, gameOptions, armies, team });
      navigate(`/league/${L.id}`);
    } catch (e) { setError(e instanceof TypeError ? "Couldn't reach the site. Check your connection and try again." : (e as Error).message); setBusy(false); }
  };

  return (
    <div className="site" style={{ fontSize: 16 }}>
      <LeagueHeader name={me || undefined} />
      <main style={{ maxWidth: 1240, margin: '0 auto', padding: '8px 24px 48px', display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start' }}>
        <section style={{ flex: '999 1 560px', minWidth: 0, display: 'grid', gap: 22 }}>
          <div>
            <p className="site-kicker">New league</p>
            <h1 className="site-display" style={{ fontSize: 44, lineHeight: 1.05, marginTop: 4 }}>Set up your league</h1>
            <p style={{ color: '#c9c7d3', maxWidth: '64ch', marginTop: 6 }}>You get one link to share. Each seat is filled by someone who opens it, or by a bot you add. A round ends when its games are played, so the league can run over an evening or a few weeks.</p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
            <label style={{ display: 'grid', gap: 8, flex: '1 1 280px', maxWidth: 460 }}>
              <span className="site-kicker">League name</span>
              <input value={name} maxLength={50} placeholder="e.g. Autumn Flag League" onChange={e => setName(e.target.value)} style={{ minHeight: 48, fontSize: 17 }} />
            </label>
            <label style={{ display: 'grid', gap: 8, flex: '0 1 220px' }}>
              <span className="site-kicker">Your name</span>
              <input value={me} maxLength={30} onChange={e => setMe(e.target.value)} style={{ minHeight: 48, fontSize: 17 }} />
            </label>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28 }}>
            <div style={{ display: 'grid', gap: 8 }}>
              <span className="site-kicker">Game</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 600, minHeight: 40 }}><span className="site-letter" style={{ width: 34, height: 34, fontSize: 20 }}>C</span>Conquer the Flag</span>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <span className="site-kicker">Players</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40 }}>
                <button type="button" className="site-btn site-btn-small" aria-label="Fewer players" style={{ width: 36, padding: 0 }} onClick={() => setSeats(Math.max(2, seats - 1))}>−</button>
                <strong style={{ minWidth: 20, textAlign: 'center', fontSize: 22 }}>{seats}</strong>
                <button type="button" className="site-btn site-btn-small" aria-label="More players" style={{ width: 36, padding: 0 }} onClick={() => setSeats(Math.min(8, seats + 1))}>+</button>
              </span>
            </div>
          </div>
          <Seg label="Format" value={format as string} onChange={v => setFormat(v as LeagueSettings['format'])}
            options={[['single', 'Round robin'], ['double', 'Double round robin'], ['knockout', 'Knockout', true], ['seeded', 'Round robin, then knockout', true]]}
            note={format === 'double' ? 'Everyone plays everyone twice, once as Blue and once as Red.' : 'Everyone plays everyone once.'} />
          <Seg label="Void a game left unplayed" value={voidAfter} onChange={setVoidAfter}
            options={[[null, 'Host decides'], [3, 'After 3 days'], [7, 'After 7 days']]}
            note={voidAfter ? 'Counted from when the rest of its round is done; then the next round starts. The host can void any game sooner. A voided game counts for neither player.' : 'Only the host voids games, from the league page. A voided game counts for neither player.'} />
          <Seg label="Armies" value={armies as string} onChange={v => setArmies(v as typeof armies)}
            options={[['builder', 'Army builder'], ['fixed', 'Same team for everyone'], ['pick', 'Each picks a team'], ['custom', 'Custom', true]]}
            note={armies === 'builder' ? 'Each player builds an army from a few choices: an anchor, a block of three core units, a striker.'
              : armies === 'fixed' ? 'Everyone plays the same built-in team. The league goes straight to round 1 when it starts.'
              : "Each player picks one of the game's built-in teams when the league starts."} />
          {armies === 'builder' && <Seg label="Choices per pick" value={choices} onChange={v => setChoices(v as 2 | 3)} options={[[2, '2 options'], [3, '3 options']]} />}
          {armies === 'fixed' && (
            <label style={{ display: 'grid', gap: 8, maxWidth: 320 }}>
              <span className="site-kicker">Team</span>
              <select value={team} onChange={e => setTeam(e.target.value)} style={{ minHeight: 44 }}>
                {TEAM_KEYS.map(k => <option key={k} value={k}>{teamName(k)}</option>)}
              </select>
            </label>
          )}
          <Seg label="Army abilities" value="off" onChange={() => undefined} options={[['off', 'Off'], ['on', 'On', true]]} />
          <Seg label="Between rounds" value={improve ? 'improve' : 'static'} onChange={v => setImprove(v === 'improve')} options={[['improve', 'Armies improve'], ['static', 'Armies stay the same']]}
            note={improve ? 'After each league game (and any round someone sits out), one of their units is drawn and they choose one of two improvements for it: a new trait or +1 Move. Permanent.' : 'Everyone plays the whole league with the army they built.'} />
          <div style={{ display: 'grid', gap: 8, maxWidth: 640 }}>
            <span className="site-kicker">Game settings</span>
            <p className="site-muted" style={{ fontSize: 14 }}>Every game in the league is played with these.</p>
            <CtfSettings config={gameConfig} onChange={setGameConfig} hideTeams hideDemo />
          </div>
        </section>

        <aside className="site-panel" style={{ flex: '1 1 320px', minWidth: 0, display: 'grid', gap: 14 }}>
          <p className="site-kicker">At a glance</p>
          <h2 className="site-display" style={{ fontSize: 28, lineHeight: 1.1 }}>{name.trim() || 'Your league'}</h2>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6, listStyle: 'disc' }}>
            <li>{games} games over {rounds} rounds; each player plays {format === 'double' ? 2 * (seats - 1) : seats - 1}{seats % 2 ? ' (one sits out each round)' : ''}</li>
            <li>{armies === 'builder' ? `Army builder, ${choices} options a pick` : armies === 'fixed' ? `Everyone plays ${teamName(team)}` : 'Each player picks a built-in team'}{improve ? '; armies improve after each game' : ''}</li>
            <li>Conquer the Flag: {gameOptionsText(gameOptions)}</li>
            <li>{voidAfter ? `Unplayed games voided ${voidAfter} days after the rest of the round` : 'Unplayed games: the host decides'}</li>
          </ul>
          <p className="site-muted site-small">Pairings are drawn when the league starts; everyone can see every round ahead.</p>
          {error && <p role="alert" style={{ color: 'var(--danger)' }}>{error}</p>}
          <button type="button" className="site-btn site-btn-primary site-btn-big" disabled={busy || !name.trim() || !me.trim()} onClick={create}>{busy ? 'Creating…' : 'Create league'}</button>
        </aside>
      </main>
    </div>
  );
}
